import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import { HealthScoreCard } from '../components/HealthScoreCard';
import { getDashboardStats, getUpcomingEvents, getCommerciauxStats, getMyMoney } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { 
    Users, 
    FolderKanban, 
    Calendar, 
    Bell, 
    TrendingUp, 
    Clock,
    FileText,
    AlertTriangle,
    CheckCircle2,
    ChevronRight,
    HardHat,
    ArrowUpRight,
    Euro,
    UserCircle,
    Trophy,
    Target
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

const StatCard = ({ title, value, icon: Icon, color, subtext, link }) => (
    <Link to={link || '#'} className="bg-white border border-[#E4E4E7] p-6 hover:border-[#52525B] transition-colors group" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
        <div className="flex items-start justify-between">
            <div>
                <p className="field-label mb-2">{title}</p>
                <p className="text-3xl font-bold text-[#0A0A0A] tracking-tight">{value}</p>
                {subtext && <p className="text-sm text-[#52525B] mt-1">{subtext}</p>}
            </div>
            <div className={`p-3 ${color}`}>
                <Icon className="w-6 h-6 text-white" />
            </div>
        </div>
        {link && (
            <div className="mt-4 flex items-center text-sm text-[#E85A24] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                Voir détails <ChevronRight className="w-4 h-4 ml-1" />
            </div>
        )}
    </Link>
);

const ActionCard = ({ title, count, icon: Icon, color, link }) => (
    <Link to={link} className="flex items-center gap-4 p-4 bg-white border border-[#E4E4E7] hover:border-[#52525B] transition-colors" data-testid={`action-${title.toLowerCase().replace(/\s/g, '-')}`}>
        <div className={`p-2 ${color}`}>
            <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
            <p className="font-medium text-[#0A0A0A]">{title}</p>
            <p className="text-sm text-[#52525B]">{count} en attente</p>
        </div>
        <ChevronRight className="w-5 h-5 text-[#52525B]" />
    </Link>
);

const UpcomingItem = ({ type, title, date, link }) => (
    <Link to={link} className="flex items-center gap-3 p-3 hover:bg-[#F4F4F5] transition-colors" data-testid={`upcoming-${type}`}>
        <div className={`w-2 h-2 rounded-full ${type === 'rdv' ? 'bg-[#E85A24]' : type === 'chantier' ? 'bg-[#16A34A]' : 'bg-[#D97706]'}`} />
        <div className="flex-1 min-w-0">
            <p className="font-medium text-[#0A0A0A] truncate">{title}</p>
            <p className="text-xs text-[#52525B]">{date}</p>
        </div>
        <ArrowUpRight className="w-4 h-4 text-[#52525B]" />
    </Link>
);

export const DashboardPage = () => {
    const { user } = useAuth();
    const location = useLocation();
    const [stats, setStats] = useState(null);
    const [upcoming, setUpcoming] = useState(null);
    const [commerciaux, setCommerciaux] = useState([]);
    const [selectedCommercialId, setSelectedCommercialId] = useState('');
    const [myStats, setMyStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';
    const isCommercial = user?.role === 'commercial';

    const fetchData = async () => {
        try {
            const promises = [
                getDashboardStats(),
                getUpcomingEvents()
            ];
            if (isAdminOrManager) {
                promises.push(getCommerciauxStats());
            }
            const results = await Promise.all(promises);
            setStats(results[0].data);
            setUpcoming(results[1].data);
            if (isAdminOrManager && results[2]) {
                setCommerciaux(results[2].data);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Initial load + refetch on every navigation back to dashboard
    useEffect(() => {
        fetchData();
    }, [isAdminOrManager, location.key]);

    // Refetch when tab regains focus (user comes back after deleting in another tab)
    useEffect(() => {
        const onFocus = () => fetchData();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [isAdminOrManager]);
    // Load personal money stats (CA, marge, prime) — available for every user
    useEffect(() => {
        if (user?.id) {
            getMyMoney().then(res => setMyStats(res.data || null)).catch(() => setMyStats(null));
        }
    }, [user?.id, location.key]);

    const filteredCommerciaux = selectedCommercialId
        ? commerciaux.filter(c => c.id === selectedCommercialId)
        : commerciaux;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
                <div className="w-8 h-8 border-4 border-[#E85A24] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const sourcesData = stats?.sources ? Object.entries(stats.sources).map(([name, value]) => ({
        name: name || 'Autre',
        value
    })) : [];

    const pipelineData = stats?.pipeline_counts ? [
        { name: 'Prospects', value: (stats.pipeline_counts.nouveau_prospect || 0) + (stats.pipeline_counts.a_qualifier || 0) },
        { name: 'RDV', value: (stats.pipeline_counts.rdv_a_planifier || 0) + (stats.pipeline_counts.rdv_planifie || 0) },
        { name: 'Devis', value: (stats.pipeline_counts.devis_a_faire || 0) + (stats.pipeline_counts.devis_envoye || 0) + (stats.pipeline_counts.relance_a_faire || 0) },
        { name: 'Chantiers', value: (stats.pipeline_counts.chantier_planifie || 0) + (stats.pipeline_counts.chantier_en_cours || 0) + (stats.pipeline_counts.chantier_termine || 0) },
        { name: 'Terminés', value: (stats.pipeline_counts.reception_effectuee || 0) + (stats.pipeline_counts.dossier_clos || 0) }
    ] : [];

    const COLORS = ['#E85A24', '#16A34A', '#D97706', '#0284C7', '#7C3AED'];

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="space-y-4 sm:space-y-6" data-testid="dashboard-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight">Tableau de bord</h1>
                    <p className="text-sm text-[#52525B] mt-1">Vue d&apos;ensemble de votre activité</p>
                </div>
                <Link to="/prospects/new" className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white px-4 sm:px-6 py-2.5 sm:py-3 font-semibold transition-colors text-sm sm:text-base text-center" data-testid="new-prospect-btn">
                    + Nouveau prospect
                </Link>
            </div>

            {/* Alerte 72h : RDV sans devis */}
            {upcoming?.rdv_sans_devis_72h?.length > 0 && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-sm" data-testid="alerte-72h">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="font-bold text-red-700">
                                {upcoming.rdv_sans_devis_72h.length} RDV sans devis depuis plus de 72h
                            </p>
                            <p className="text-sm text-red-700/80 mb-2">
                                Action immédiate requise — ne perdez pas ces prospects !
                            </p>
                            <div className="flex flex-col gap-1 mt-2">
                                {upcoming.rdv_sans_devis_72h.slice(0, 5).map(r => (
                                    <Link
                                        key={r.id}
                                        to={`/dossiers/${r.id}`}
                                        className="text-sm text-red-800 hover:underline flex items-center gap-2"
                                        data-testid={`alerte-72h-item-${r.id}`}
                                    >
                                        <ChevronRight className="w-3 h-3" />
                                        <span className="font-medium">{r.prospect_nom || r.titre}</span>
                                        <span className="text-xs text-red-600/70">
                                            — RDV du {r.date_rdv ? new Date(r.date_rdv).toLocaleDateString('fr-FR') : '-'}
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Money widgets — every user sees their own CA / marge / prime */}
            {myStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-testid="money-widgets">
                    {/* CA signé du mois */}
                    <div className="bg-white border border-[#E4E4E7] p-4 sm:p-5" data-testid="money-ca-signe">
                        <div className="flex items-center justify-between mb-2">
                            <p className="field-label text-xs">CA signé</p>
                            <div className="p-1.5 bg-[#16A34A]/10 rounded-sm"><Trophy className="w-4 h-4 text-[#16A34A]" /></div>
                        </div>
                        <p className="text-xl sm:text-2xl font-bold text-[#0A0A0A] tracking-tight">
                            {(myStats.ca_signe_mois || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                        </p>
                        <p className="text-xs text-[#52525B] mt-1">
                            {myStats.nb_signatures_mois || 0} signature{(myStats.nb_signatures_mois || 0) > 1 ? 's' : ''} ce mois
                        </p>
                    </div>

                    {/* CA facturé du mois */}
                    <div className="bg-white border border-[#E4E4E7] p-4 sm:p-5" data-testid="money-ca-facture">
                        <div className="flex items-center justify-between mb-2">
                            <p className="field-label text-xs">Facturé</p>
                            <div className="p-1.5 bg-[#0284C7]/10 rounded-sm"><Euro className="w-4 h-4 text-[#0284C7]" /></div>
                        </div>
                        <p className={`text-xl sm:text-2xl font-bold tracking-tight ${(myStats.ca_facture_mois || 0) < 0 ? 'text-[#DC2626]' : 'text-[#0A0A0A]'}`}>
                            {(myStats.ca_facture_mois || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                        </p>
                        <p className="text-xs text-[#52525B] mt-1">
                            {(myStats.ca_facture_mois || 0) < 0 ? 'crédit en cours (avoirs)' : 'émis ce mois'}
                        </p>
                    </div>

                    {/* Marge brute */}
                    <div className="bg-white border border-[#E4E4E7] p-4 sm:p-5" data-testid="money-marge">
                        <div className="flex items-center justify-between mb-2">
                            <p className="field-label text-xs">Marge brute</p>
                            <div className="p-1.5 bg-[#D97706]/10 rounded-sm"><TrendingUp className="w-4 h-4 text-[#D97706]" /></div>
                        </div>
                        <p className={`text-xl sm:text-2xl font-bold tracking-tight ${(myStats.marge_brute_mois || 0) < 0 ? 'text-[#DC2626]' : 'text-[#0A0A0A]'}`}>
                            {(myStats.marge_brute_mois || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                        </p>
                        <p className="text-xs text-[#52525B] mt-1">
                            {(myStats.ca_facture_mois || 0) > 0 ? `${myStats.marge_pourcentage || 0} % du facturé` : '—'}
                        </p>
                    </div>

                    {/* Prime du mois (mise en avant) */}
                    <div className="bg-gradient-to-br from-[#7C3AED] to-[#5B21B6] text-white p-4 sm:p-5" data-testid="money-prime">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs uppercase tracking-wide opacity-80">Ma prime</p>
                            <div className="p-1.5 bg-white/20 rounded-sm"><Trophy className="w-4 h-4" /></div>
                        </div>
                        <p className="text-xl sm:text-2xl font-bold tracking-tight">
                            {(myStats.prime_mois || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                        </p>
                        <p className="text-xs opacity-90 mt-1">
                            {myStats.palier_actuel
                                ? `Palier ${(myStats.palier_actuel.rate * 100).toFixed(1)} %`
                                : 'Hors palier'}
                        </p>
                    </div>
                </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1">
                    <HealthScoreCard />
                </div>
                <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <StatCard
                        title="PROSPECTS"
                        value={stats?.totals?.prospects || 0}
                        icon={Users}
                        color="bg-[#E85A24]"
                        subtext={`+${stats?.monthly?.nouveaux_prospects || 0} ce mois`}
                        link="/prospects?is_client=false"
                />
                <StatCard
                    title="CLIENTS"
                    value={stats?.totals?.clients || 0}
                    icon={CheckCircle2}
                    color="bg-[#16A34A]"
                    link="/prospects?is_client=true"
                />
                <StatCard
                    title="CHANTIERS EN COURS"
                    value={stats?.chantiers?.en_cours || 0}
                    icon={HardHat}
                    color="bg-[#0284C7]"
                    subtext={`${stats?.chantiers?.planifies || 0} planifiés`}
                    link="/projets"
                />
                    <StatCard
                        title="TAUX TRANSFORMATION"
                        value={`${stats?.taux_transformation || 0}%`}
                        icon={TrendingUp}
                        color="bg-[#7C3AED]"
                        link="/reporting"
                    />
                </div>
            </div>

            {/* Actions requises */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <ActionCard
                    title="Relances en retard"
                    count={stats?.actions_requises?.relances_en_retard || 0}
                    icon={AlertTriangle}
                    color="bg-[#DC2626]"
                    link="/agenda"
                />
                <ActionCard
                    title="Devis à faire"
                    count={stats?.actions_requises?.devis_a_faire || 0}
                    icon={FileText}
                    color="bg-[#D97706]"
                    link="/projets"
                />
                <ActionCard
                    title="RDV à planifier"
                    count={stats?.actions_requises?.rdv_a_planifier || 0}
                    icon={Calendar}
                    color="bg-[#E85A24]"
                    link="/projets"
                />
            </div>

            {/* Charts & Upcoming */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Pipeline Chart */}
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <h3 className="font-bold text-[#0A0A0A] mb-4">Pipeline par étape</h3>
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={pipelineData}>
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip />
                            <Bar dataKey="value" fill="#E85A24" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Sources Chart */}
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <h3 className="font-bold text-[#0A0A0A] mb-4">Sources des leads</h3>
                    {sourcesData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                                <Pie
                                    data={sourcesData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={40}
                                    outerRadius={80}
                                    paddingAngle={2}
                                    dataKey="value"
                                >
                                    {sourcesData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-[200px] flex items-center justify-center text-[#52525B]">
                            Aucune donnée
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-4">
                        {sourcesData.slice(0, 5).map((item, index) => (
                            <span key={item.name} className="text-xs flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                                {item.name}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Upcoming Events */}
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <h3 className="font-bold text-[#0A0A0A] mb-4">À venir cette semaine</h3>
                    <div className="space-y-1">
                        {upcoming?.rdvs?.slice(0, 2).map((rdv) => (
                            <UpcomingItem
                                key={rdv.id}
                                type="rdv"
                                title={`RDV - ${rdv.prospect_nom || rdv.titre}`}
                                date={formatDate(rdv.date_rdv)}
                                link={`/dossiers/${rdv.id}`}
                            />
                        ))}
                        {upcoming?.chantiers?.slice(0, 2).map((chantier) => (
                            <UpcomingItem
                                key={chantier.id}
                                type="chantier"
                                title={`Chantier - ${chantier.prospect_nom || chantier.titre}`}
                                date={formatDate(chantier.date_debut_chantier)}
                                link={`/dossiers/${chantier.id}`}
                            />
                        ))}
                        {upcoming?.relances?.slice(0, 2).map((relance) => (
                            <UpcomingItem
                                key={relance.id}
                                type="relance"
                                title={`Relance - ${relance.dossier_titre || 'Dossier'}`}
                                date={formatDate(relance.date_prevue)}
                                link={`/dossiers/${relance.dossier_id}`}
                            />
                        ))}
                        {!upcoming?.rdvs?.length && !upcoming?.chantiers?.length && !upcoming?.relances?.length && (
                            <p className="text-[#52525B] text-sm py-4 text-center">Aucun événement prévu</p>
                        )}
                    </div>
                    <Link to="/agenda" className="block mt-4 text-center text-sm text-[#E85A24] font-medium hover:underline">
                        Voir l&apos;agenda complet
                    </Link>
                </div>
            </div>

            {/* Performance Commerciaux - Admin/Manager only */}
            {isAdminOrManager && commerciaux.length > 0 && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <h2 className="text-xl font-bold text-[#0A0A0A] tracking-tight flex items-center gap-2">
                            <UserCircle className="w-6 h-6 text-[#7C3AED]" />
                            Performance par Commercial
                        </h2>
                        <select
                            className="h-10 rounded-sm border border-[#E4E4E7] px-3 text-sm bg-white"
                            value={selectedCommercialId}
                            onChange={(e) => setSelectedCommercialId(e.target.value)}
                            data-testid="commercial-filter-select"
                        >
                            <option value="">Tous les commerciaux</option>
                            {commerciaux.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredCommerciaux.map((commercial) => (
                            <div 
                                key={commercial.id} 
                                className="bg-white border-2 border-[#7C3AED]/20 p-6 hover:border-[#7C3AED] transition-colors"
                                data-testid={`commercial-card-${commercial.id}`}
                            >
                                {/* Header */}
                                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[#E4E4E7]">
                                    <div className="w-12 h-12 rounded-full bg-[#7C3AED] flex items-center justify-center text-white text-lg font-bold">
                                        {commercial.name.charAt(0)}
                                    </div>
                                    <div>
                                        <p className="font-semibold text-[#0A0A0A]">{commercial.name}</p>
                                        <p className="text-sm text-[#52525B] capitalize">{commercial.role}</p>
                                    </div>
                                </div>
                                
                                {/* Stats Grid */}
                                <div className="grid grid-cols-2 gap-3">
                                    <Link 
                                        to={`/prospects?commercial_id=${commercial.id}`}
                                        className="p-3 bg-[#F4F4F5] hover:bg-[#E4E4E7] transition-colors"
                                    >
                                        <p className="text-2xl font-bold text-[#E85A24]">{commercial.prospects}</p>
                                        <p className="text-xs text-[#52525B] uppercase font-medium">Prospects</p>
                                    </Link>
                                    <Link 
                                        to={`/prospects?commercial_id=${commercial.id}&is_client=true`}
                                        className="p-3 bg-[#F4F4F5] hover:bg-[#E4E4E7] transition-colors"
                                    >
                                        <p className="text-2xl font-bold text-[#16A34A]">{commercial.clients}</p>
                                        <p className="text-xs text-[#52525B] uppercase font-medium">Clients</p>
                                    </Link>
                                    <div className="p-3 bg-[#F4F4F5]">
                                        <p className="text-2xl font-bold text-[#0284C7]">{commercial.dossiers}</p>
                                        <p className="text-xs text-[#52525B] uppercase font-medium">Dossiers</p>
                                    </div>
                                    <div className="p-3 bg-[#F4F4F5]">
                                        <p className="text-2xl font-bold text-[#D97706]">{commercial.chantiers_en_cours}</p>
                                        <p className="text-xs text-[#52525B] uppercase font-medium">Chantiers</p>
                                    </div>
                                </div>
                                
                                {/* Transformation Rate */}
                                <div className="mt-4 pt-4 border-t border-[#E4E4E7]">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm text-[#52525B]">Taux transformation</span>
                                        <span className={`text-lg font-bold ${commercial.taux_transformation >= 50 ? 'text-[#16A34A]' : commercial.taux_transformation >= 25 ? 'text-[#D97706]' : 'text-[#DC2626]'}`}>
                                            {commercial.taux_transformation}%
                                        </span>
                                    </div>
                                    <div className="w-full bg-[#E4E4E7] h-2 rounded-full overflow-hidden">
                                        <div 
                                            className={`h-full transition-all ${commercial.taux_transformation >= 50 ? 'bg-[#16A34A]' : commercial.taux_transformation >= 25 ? 'bg-[#D97706]' : 'bg-[#DC2626]'}`}
                                            style={{ width: `${Math.min(commercial.taux_transformation, 100)}%` }}
                                        />
                                    </div>
                                </div>
                                
                                {/* CA Facturé mois + Marge + Prime */}
                                <div className="mt-4 space-y-2">
                                    <div className="flex items-center justify-between p-3 bg-[#16A34A]/10 rounded-sm">
                                        <span className="text-sm font-medium text-[#16A34A]">CA facturé (mois)</span>
                                        <span className="flex items-center gap-1 text-lg font-bold text-[#16A34A]">
                                            <Euro className="w-4 h-4" />
                                            {(commercial.ca_facture_mois || 0).toLocaleString('fr-FR')}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-[#F4F4F5] rounded-sm text-sm">
                                        <span className="text-[#52525B]">Marge brute</span>
                                        <span className="font-semibold">
                                            {(commercial.marge_brute_mois || 0).toLocaleString('fr-FR')} € ({commercial.marge_pourcentage || 0}%)
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-[#7C3AED]/10 rounded-sm">
                                        <span className="text-sm font-medium text-[#7C3AED] flex items-center gap-1">
                                            <Trophy className="w-4 h-4" /> Prime estimée
                                        </span>
                                        <span className="font-bold text-[#7C3AED]">
                                            {(commercial.prime_mois || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                        </span>
                                    </div>
                                    {commercial.palier_actuel && (
                                        <p className="text-xs text-[#52525B] text-center">
                                            Palier : {(commercial.palier_actuel.rate * 100).toFixed(1)}% ({commercial.palier_actuel.min.toLocaleString('fr-FR')}–{commercial.palier_actuel.max.toLocaleString('fr-FR')} €)
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
